import { useMemo, useState } from 'react';
import {
  BrainPocketArticle,
  BrainPocketStore,
  emptyStore,
  loadStore,
  saveStore,
  validateImportData,
} from '../utils/BrainPocket/brainPocketStorage';
import { parseSmartUrl } from '../utils/BrainPocket/brainPocketUrl';

export type DomainEntry = [string, { articles: BrainPocketArticle[] }];

const SUMMARY_TIMEOUT_MS = 9_000;
const productionBrainPocketApiBaseUrl = 'https://brain-pocket-anbnbsbsh7e2aag0.westeurope-01.azurewebsites.net';
const brainPocketApiBaseUrl = process.env.REACT_APP_BRAINPOCKET_API_BASE_URL
  || (process.env.NODE_ENV === 'production' ? productionBrainPocketApiBaseUrl : '');

function uid() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `article-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function cloneStore(store: BrainPocketStore): BrainPocketStore {
  return {
    domains: Object.fromEntries(
      Object.entries(store.domains).map(([domain, data]) => [
        domain,
        {
          articles: data.articles.map((article) => ({
            ...article,
            keyPoints: [...article.keyPoints],
          })),
        },
      ]),
    ),
  };
}

function sanitizeKeyPoints(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((point): point is string => typeof point === 'string')
    .map((point) => point.trim())
    .filter(Boolean)
    .slice(0, 3);
}

async function fetchAutomatedKeyPoints(url: string): Promise<string[]> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), SUMMARY_TIMEOUT_MS);

  try {
    const response = await fetch(`${brainPocketApiBaseUrl}/api/summarize`, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return sanitizeKeyPoints(data.keyPoints);
  } catch {
    return [];
  } finally {
    window.clearTimeout(timeout);
  }
}

export function useBrainPocket() {
  const [store, setStoreState] = useState<BrainPocketStore>(() => (
    typeof window === 'undefined' ? emptyStore : loadStore()
  ));
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [domainOpen, setDomainOpen] = useState<Record<string, boolean>>({});
  const [importError, setImportError] = useState<string | null>(null);

  function setStore(nextStore: BrainPocketStore) {
    setStoreState(nextStore);
    saveStore(nextStore);
  }

  function updateStore(updater: (previous: BrainPocketStore) => BrainPocketStore) {
    setStoreState((previous) => {
      const next = updater(previous);
      saveStore(next);
      return next;
    });
  }

  const selectedArticle = useMemo(() => {
    for (const domain of Object.values(store.domains)) {
      const found = domain.articles.find((article) => article.id === selectedArticleId);
      if (found) {
        return found;
      }
    }

    return null;
  }, [store, selectedArticleId]);

  const totals = useMemo(() => {
    const articles = Object.values(store.domains).flatMap((domain) => domain.articles);
    const keyPoints = articles.reduce((sum, article) => sum + article.keyPoints.length, 0);

    return {
      domains: Object.keys(store.domains).length,
      articles: articles.length,
      keyPoints,
      unread: articles.filter((article) => !article.read).length,
    };
  }, [store]);

  async function addArticle({ urlInput, titleInput }: { urlInput: string; titleInput: string }) {
    const { domain, cleanUrl, title } = parseSmartUrl(urlInput, titleInput);
    const keyPoints = await fetchAutomatedKeyPoints(cleanUrl);
    const article: BrainPocketArticle = {
      id: uid(),
      title,
      url: cleanUrl,
      savedAt: new Date().toISOString().slice(0, 10),
      read: false,
      keyPoints,
    };

    updateStore((previous) => {
      const next = cloneStore(previous);
      if (!next.domains[domain]) {
        next.domains[domain] = { articles: [] };
      }

      next.domains[domain].articles = [
        article,
        ...next.domains[domain].articles.filter((item) => item.url !== cleanUrl),
      ];
      return next;
    });

    setDomainOpen((previous) => ({ ...previous, [domain]: true }));
    setSelectedArticleId(article.id);
    setImportError(null);
  }

  function updateArticle(articleId: string, updater: (article: BrainPocketArticle) => BrainPocketArticle) {
    updateStore((previous) => {
      const next = cloneStore(previous);

      for (const domainKey of Object.keys(next.domains)) {
        const index = next.domains[domainKey].articles.findIndex((article) => article.id === articleId);
        if (index !== -1) {
          next.domains[domainKey].articles[index] = updater(next.domains[domainKey].articles[index]);
          return next;
        }
      }

      return previous;
    });
  }

  function deleteArticle(articleId: string) {
    updateStore((previous) => {
      const next = cloneStore(previous);

      for (const domainKey of Object.keys(next.domains)) {
        next.domains[domainKey].articles = next.domains[domainKey].articles.filter(
          (article) => article.id !== articleId,
        );

        if (next.domains[domainKey].articles.length === 0) {
          delete next.domains[domainKey];
        }
      }

      return next;
    });

    if (selectedArticleId === articleId) {
      setSelectedArticleId(null);
    }
  }

  function toggleRead(articleId: string) {
    updateArticle(articleId, (article) => ({ ...article, read: !article.read }));
  }

  function addKeyPoint(articleId: string, point: string) {
    const trimmed = point.trim();
    if (!trimmed) {
      return;
    }

    updateArticle(articleId, (article) => ({
      ...article,
      keyPoints: [...article.keyPoints, trimmed],
    }));
  }

  function deleteKeyPoint(articleId: string, index: number) {
    updateArticle(articleId, (article) => ({
      ...article,
      keyPoints: article.keyPoints.filter((_, pointIndex) => pointIndex !== index),
    }));
  }

  async function importBackup(file: File) {
    try {
      const parsed = JSON.parse(await file.text());
      if (!validateImportData(parsed)) {
        throw new Error('Invalid backup structure');
      }

      setStore(parsed);
      setSelectedArticleId(null);
      setDomainOpen({});
      setImportError(null);
    } catch {
      setImportError('That file does not look like a BrainPocket backup.');
    }
  }

  const filteredDomains = useMemo<DomainEntry[]>(() => {
    const query = search.toLowerCase().trim();
    const entries = Object.entries(store.domains).sort(([domainA], [domainB]) => (
      domainA.localeCompare(domainB)
    ));

    if (!query) {
      return entries;
    }

    return entries
      .map<DomainEntry>(([domain, value]) => {
        const articles = value.articles.filter((article) => (
          domain.toLowerCase().includes(query)
          || article.title.toLowerCase().includes(query)
          || article.url.toLowerCase().includes(query)
          || article.keyPoints.some((point) => point.toLowerCase().includes(query))
        ));

        return [domain, { articles }];
      })
      .filter(([, value]) => value.articles.length > 0);
  }, [store, search]);

  return {
    store,
    search,
    setSearch,
    domainOpen,
    setDomainOpen,
    filteredDomains,
    selectedArticle,
    selectedArticleId,
    setSelectedArticleId,
    totals,
    importError,
    addArticle,
    deleteArticle,
    toggleRead,
    addKeyPoint,
    deleteKeyPoint,
    importBackup,
  };
}
